import { useEffect, useRef, useState } from 'react';
import { ChevronDown, X } from 'lucide-react';
import { cn } from './cn';

export interface SelectOption {
  value: string;
  label: string;
}

export interface SelectProps {
  value: string;
  onChange: (value: string) => void;
  options: SelectOption[];
  className?: string;
  /** Open the option list on mount — used when a filter is added on demand. */
  defaultOpen?: boolean;
  /** When set, a small × replaces the chevron inside the box and calls this (removes an on-demand filter). */
  onRemove?: () => void;
  removeLabel?: string;
  /** Show the ▾ chevron (default). Filter-bar dropdowns hide it — the whole box is the click target. */
  showChevron?: boolean;
  /** Open the option list above the trigger — for selects that sit at the bottom of a card or table. */
  dropUp?: boolean;
}

export function Select({ value, onChange, options, className, defaultOpen = false, onRemove, removeLabel = 'Remove filter', showChevron = true, dropUp = false }: SelectProps) {
  const [open, setOpen] = useState(defaultOpen);
  const ref = useRef<HTMLDivElement>(null);

  const selected = options.find((o) => o.value === value);

  useEffect(() => {
    if (!open) return;
    const onMouse = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onMouse);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onMouse);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <div ref={ref} className={cn('relative', className)}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center gap-1.5 rounded-md border border-border-default bg-surface-1 px-2.5 py-1 text-xs text-text-primary focus:outline-none focus:ring-2 focus:ring-border-focus/20"
      >
        <span className="flex-1 text-left">{selected?.label ?? value}</span>
        {onRemove ? (
          <span
            role="button"
            tabIndex={0}
            aria-label={removeLabel}
            onClick={(e) => { e.stopPropagation(); onRemove(); }}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); e.stopPropagation(); onRemove(); } }}
            className="flex h-4 w-4 shrink-0 items-center justify-center rounded text-text-tertiary hover:bg-surface-2 hover:text-text-primary"
          >
            <X className="h-3 w-3" />
          </span>
        ) : showChevron ? (
          <ChevronDown
            className={cn(
              'h-3 w-3 text-text-tertiary transition-transform duration-base',
              open && 'rotate-180',
            )}
          />
        ) : null}
      </button>

      {open && (
        <ul
          role="listbox"
          className={cn(
            'absolute left-0 z-50 min-w-full max-h-52 overflow-y-auto rounded-md border border-border-default bg-surface-1 py-0.5',
            dropUp ? 'bottom-full mb-1' : 'top-full mt-1',
          )}
        >
          {options.map((opt) => (
            <li
              key={opt.value}
              role="option"
              aria-selected={opt.value === value}
              onClick={() => {
                onChange(opt.value);
                setOpen(false);
              }}
              className={cn(
                'cursor-pointer whitespace-nowrap px-2.5 py-1.5 text-xs transition-colors duration-fast',
                opt.value === value
                  ? 'bg-surface-2 text-text-primary'
                  : 'text-text-secondary hover:bg-surface-2 hover:text-text-primary',
              )}
            >
              {opt.label}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
